import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { Building2, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-5">
      <div
        className="hidden lg:block col-span-3 relative"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(10,17,36,0.85), rgba(24,106,101,0.6)), url('https://images.unsplash.com/photo-1487958449943-2429e8be8625?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center font-black text-lg" style={{ background: "var(--brand-ochre)" }}>SS</div>
            <div>
              <div className="font-display font-black text-lg tracking-tight">SHREE SAMARTH</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">Consultancy CRM</div>
            </div>
          </div>
          <div>
            <div className="max-w-lg font-display text-4xl font-black leading-tight">
              Operate your consultancy<br />
              <span className="text-[color:var(--brand-ochre)]">with precision.</span>
            </div>
            <p className="mt-4 max-w-md text-white/70 text-sm">
              Customers, agents, receipts, and staff — all in one place. Built for consultancy operators who need clarity, speed, and control.
            </p>
          </div>
          <div className="text-[11px] text-white/50 uppercase tracking-widest">
            © {new Date().getFullYear()} SHREE SAMARTH CONSULTANCY
          </div>
        </div>
      </div>

      <div className="col-span-2 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="flex lg:hidden items-center gap-2 mb-6">
            <Building2 className="h-5 w-5 text-[color:var(--brand-teal)]" />
            <div className="font-display font-black text-lg">SHREE SAMARTH</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sign in</div>
            <h1 className="font-display text-3xl font-black tracking-tight mt-1">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Use your admin credentials to continue.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
            <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">Password</Label>
            <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" />
          </div>
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] text-white font-semibold tracking-wide"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
